import React from 'react';
import { Layers, Link, Plus, X } from 'lucide-react';

const CdsCompositeWizard = ({ data = {}, setData }) => {

  const agregarAssociation = () => {
    const assoc = {
      id: Date.now(),
      nombre: '',
      targetCDS: '',
      cardinalidad: '[0..*]',
      campoLocal: '',
      campoTarget: '',
    };
    setData({ ...data, compositeAssociations: [...(data.compositeAssociations || []), assoc] });
  };

  const actualizarAssociation = (id, campo, valor) => {
    setData({
      ...data,
      compositeAssociations: (data.compositeAssociations || []).map(a =>
        a.id === id ? { ...a, [campo]: valor } : a
      ),
    });
  };

  const eliminarAssociation = (id) => {
    setData({ ...data, compositeAssociations: (data.compositeAssociations || []).filter(a => a.id !== id) });
  };

  const inputClass = "w-full p-2 bg-slate-800 border border-slate-700 rounded-lg text-xs font-mono uppercase outline-none focus:border-violet-400 text-white placeholder:text-slate-600";
  const selectClass = "w-full p-2.5 bg-slate-800 border-none rounded-xl text-xs font-bold outline-none text-white";
  const labelClass = "text-[9px] font-bold text-slate-400 uppercase block mb-1";

  return (
    <div className="animate-in fade-in slide-in-from-top-4 duration-500 text-left space-y-6">

      {/* NOTA */}
      <div className="bg-violet-900/10 border-l-4 border-violet-600 p-5 rounded-r-3xl flex gap-4 items-start">
        <div className="bg-violet-600 p-2 rounded-xl text-white shrink-0"><Layers size={18} /></div>
        <div>
          <p className="text-[11px] text-violet-400 font-black uppercase tracking-widest mb-1">CDS Composite Guide</p>
          <p className="text-[11px] text-slate-400 leading-relaxed">
            Este wizard genera una vista <strong>COMPOSITE</strong> que une dos o más CDS existentes, 
            más una vista <strong>CONSUMPTION</strong> con anotaciones Fiori. 
            No genera Interface Views — las fuentes ya existen en tu sistema.
          </p>
        </div>
      </div>

      {/* FILA 1 — IDENTIFICADORES */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* CDS FUENTE + COMPOSITE */}
        <div className="bg-[#0F172A] p-6 rounded-[28px] text-white border-b-4 border-violet-500 space-y-4">
          <label className="text-[10px] font-black text-violet-400 uppercase flex items-center gap-2 border-b border-slate-800 pb-2">
            <Layers size={14} /> Identificadores
          </label>

          <div>
            <label className={labelClass}>Nombre Vista COMPOSITE (ZC_...)</label>
            <input type="text" className={inputClass} placeholder="ZC_SalesOrderFull"
              value={data.compositeName || ''}
              onChange={e => setData({ ...data, compositeName: e.target.value })} />
          </div>

          <div>
            <label className={labelClass}>Nombre Vista SQL COMPOSITE (máx 16 chars)</label>
            <input type="text" className={inputClass} placeholder="ZVSALESFULLL"
              value={data.compositeSqlView || ''}
              onChange={e => setData({ ...data, compositeSqlView: e.target.value })} />
          </div>

          <div>
            <label className={labelClass}>CDS Fuente Principal (ya existe en el sistema)</label>
            <input type="text" className={inputClass} placeholder="ZI_SalesOrder"
              value={data.compositeFuente || ''}
              onChange={e => setData({ ...data, compositeFuente: e.target.value })} />
          </div>

          <div>
            <label className={labelClass}>Descripción (@EndUserText.label)</label>
            <input type="text" className={inputClass} placeholder="Sales Order Composite View"
              value={data.compositeLabel || ''}
              onChange={e => setData({ ...data, compositeLabel: e.target.value })} />
          </div>
        </div>

        {/* CONSUMPTION */}
        <div className="bg-[#0F172A] p-6 rounded-[28px] text-white border-b-4 border-violet-500 space-y-4">
          <label className="text-[10px] font-black text-violet-400 uppercase flex items-center gap-2 border-b border-slate-800 pb-2">
            <Layers size={14} /> Consumption View
          </label>

          <div>
            <label className={labelClass}>Nombre Vista CONSUMPTION (ZCC_...)</label>
            <input type="text" className={inputClass} placeholder="ZCC_SalesOrderFull"
              value={data.consumptionName || ''}
              onChange={e => setData({ ...data, consumptionName: e.target.value })} />
          </div>

          <div>
            <label className={labelClass}>Nombre Vista SQL CONSUMPTION (máx 16 chars)</label>
            <input type="text" className={inputClass} placeholder="ZVCSALESFULLL"
              value={data.consumptionSqlView || ''}
              onChange={e => setData({ ...data, consumptionSqlView: e.target.value })} />
          </div>

          <div>
            <label className={labelClass}>@UI.headerInfo — Campo Título</label>
            <input type="text" className={inputClass} placeholder="SalesOrder"
              value={data.compositeHeaderTitle || ''}
              onChange={e => setData({ ...data, compositeHeaderTitle: e.target.value })} />
          </div>

          <div>
            <label className={labelClass}>@UI.headerInfo — Campo Descripción</label>
            <input type="text" className={inputClass} placeholder="CustomerId"
              value={data.compositeHeaderDesc || ''}
              onChange={e => setData({ ...data, compositeHeaderDesc: e.target.value })} />
          </div>

          {/* OData */}
          <label className="flex items-center gap-3 cursor-pointer p-3 bg-slate-800 rounded-xl hover:bg-slate-700 transition-all">
            <div className="flex-1">
              <p className="text-[10px] font-black text-white uppercase">OData Exposure</p>
              <p className="text-[9px] text-slate-400">@OData.publish: true en Consumption View</p>
            </div>
            <input type="checkbox" className="w-4 h-4 rounded"
              checked={data.compositeOdata || false}
              onChange={() => setData({ ...data, compositeOdata: !data.compositeOdata })} />
          </label>

          {/* Metadata Extension */}
          <label className="flex items-center gap-3 cursor-pointer p-3 bg-slate-800 rounded-xl hover:bg-slate-700 transition-all">
            <div className="flex-1">
              <p className="text-[10px] font-black text-white uppercase">Metadata Extension</p>
              <p className="text-[9px] text-slate-400">Genera objeto separado @Metadata.layer: #CUSTOMER</p>
            </div>
            <input type="checkbox" className="w-4 h-4 rounded"
              checked={data.compositeMetadataExt || false}
              onChange={() => setData({ ...data, compositeMetadataExt: !data.compositeMetadataExt })} />
          </label>
        </div>
      </div>

      {/* ASSOCIATIONS — CDS adicionales */}
      <div className="bg-[#0F172A] p-6 rounded-[28px] text-white border-b-4 border-cyan-500">
        <div className="flex items-center justify-between border-b border-slate-800 pb-3 mb-4">
          <label className="text-[10px] font-black text-cyan-400 uppercase flex items-center gap-2">
            <Link size={14} /> CDS Adicionales (Associations)
          </label>
          <button onClick={agregarAssociation}
            className="flex items-center gap-2 px-3 py-1.5 bg-cyan-600/20 border border-cyan-500/30 rounded-xl text-cyan-400 text-[10px] font-black uppercase hover:bg-cyan-600/30 transition-all">
            <Plus size={12} /> Agregar CDS
          </button>
        </div>

        {(data.compositeAssociations || []).length === 0 ? (
          <p className="text-[10px] text-slate-600 italic text-center py-4">
            Agregá los CDS existentes que querés unir via associations
          </p>
        ) : (
          <div className="space-y-4">
            {(data.compositeAssociations || []).map((assoc) => (
              <div key={assoc.id} className="bg-slate-800/50 border border-slate-700 rounded-2xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-black text-cyan-400 uppercase">Association</span>
                  <button onClick={() => eliminarAssociation(assoc.id)} className="text-slate-600 hover:text-red-400 transition-colors">
                    <X size={14} />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelClass}>Nombre (_Items)</label>
                    <input type="text" className={inputClass} placeholder="_Items"
                      value={assoc.nombre}
                      onChange={e => actualizarAssociation(assoc.id, 'nombre', e.target.value)} />
                  </div>
                  <div>
                    <label className={labelClass}>CDS Target (existente)</label>
                    <input type="text" className={inputClass} placeholder="ZI_SalesItem"
                      value={assoc.targetCDS}
                      onChange={e => actualizarAssociation(assoc.id, 'targetCDS', e.target.value)} />
                  </div>
                  <div>
                    <label className={labelClass}>Cardinalidad</label>
                    <select className={selectClass} value={assoc.cardinalidad}
                      onChange={e => actualizarAssociation(assoc.id, 'cardinalidad', e.target.value)}>
                      <option value="[0..1]">[0..1] — Cero o uno</option>
                      <option value="[1..1]">[1..1] — Exactamente uno</option>
                      <option value="[0..*]">[0..*] — Cero o muchos</option>
                      <option value="[1..*]">[1..*] — Uno o muchos</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelClass}>Campo Local (ON)</label>
                    <input type="text" className={inputClass} placeholder="SalesOrder"
                      value={assoc.campoLocal}
                      onChange={e => actualizarAssociation(assoc.id, 'campoLocal', e.target.value)} />
                  </div>
                  <div>
                    <label className={labelClass}>Campo Target (ON)</label>
                    <input type="text" className={inputClass} placeholder="SalesOrder"
                      value={assoc.campoTarget}
                      onChange={e => actualizarAssociation(assoc.id, 'campoTarget', e.target.value)} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* CAMPOS A PROYECTAR */}
      <div className="bg-[#0F172A] p-6 rounded-[28px] text-white border-b-4 border-amber-500 space-y-4">
        <label className="text-[10px] font-black text-amber-400 uppercase flex items-center gap-2 border-b border-slate-800 pb-2">
          <Layers size={14} /> Campos & Proyección
        </label>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Campos a proyectar de la fuente principal</label>
            <textarea
              className="w-full p-3 bg-slate-800 rounded-xl text-[11px] font-mono text-white placeholder:text-slate-600 outline-none resize-none h-24"
              placeholder={"SalesOrder,\nCustomerId,\nCreationDate,\nNetAmount,\nCurrency"}
              value={data.compositeCampos || ''}
              onChange={e => setData({ ...data, compositeCampos: e.target.value })} />
          </div>

          <div>
            <label className={labelClass}>Associations a proyectar al final</label>
            <textarea
              className="w-full p-3 bg-slate-800 rounded-xl text-[11px] font-mono text-white placeholder:text-slate-600 outline-none resize-none h-24"
              placeholder={"_Items,\n_Customer,\n_CompanyCode"}
              value={data.compositeProyeccion || ''}
              onChange={e => setData({ ...data, compositeProyeccion: e.target.value })} />
          </div>

          <div>
            <label className={labelClass}>Auth Check</label>
            <select className={selectClass}
              value={data.compositeAuthCheck || '#CHECK'}
              onChange={e => setData({ ...data, compositeAuthCheck: e.target.value })}>
              <option value="#NOT_REQUIRED">#NOT_REQUIRED</option>
              <option value="#CHECK">#CHECK</option>
              <option value="#PRIVILEGED_ONLY">#PRIVILEGED_ONLY</option>
            </select>
          </div>

          <div>
            <label className={labelClass}>Filtro WHERE (opcional)</label>
            <input type="text" className={inputClass}
              placeholder="mandt = $session.client"
              value={data.compositeFiltro || ''}
              onChange={e => setData({ ...data, compositeFiltro: e.target.value })} />
          </div>
        </div>
      </div>

    </div>
  );
};

export default CdsCompositeWizard;